import {CheckboxContext, useCheckboxSelection} from "../Checkbox";
import {Button, Grid, Menu, MenuItem, TextField, Tooltip} from "@mui/material";
import {useContext, useState} from "react";
import {useAsyncAction} from "nate-react-api-helpers";
import {api} from "../../../../api/API";
import {useTableData, useTableRefresher} from "../../../../misc/scroller/Table";
import {logisticsTableName} from "./Logistics";
import {Loader} from "../../../../misc/Loader";
import {useProjectId} from "../ProjectName";
import {InventoryRequest} from "../../../../api/Logistics";
import {ChangesetContext} from "../changeset/ChangesetContext";
import {useSnackbar} from "../../../../misc/Snackbar";

// a logistics item can be removed here once its opening is gone and nothing has
// acted on it yet, see RemoveOrphanedRequests for the authoritative check
export function isOrphanedRemovable(row: InventoryRequest) {
    return row.openingArchived && (row.status === "not-released" || row.status === "released");
}

export function BulkRemoveOrphaned(props: {
    type: string;
    anchorEl: HTMLElement | null;
    onOpen(): void;
}) {
    const sel = useCheckboxSelection()
    const setChecked = useContext(CheckboxContext).set;
    const [open, setOpen] = useState(false);
    const [note, setNote] = useState("");
    const project = useProjectId();
    const changeset = useContext(ChangesetContext);
    const snack = useSnackbar();
    const refresher = useTableRefresher(logisticsTableName(project, props.type));
    const data = useTableData(logisticsTableName(project, props.type)) as InventoryRequest[]

    const values = data.filter(d => sel.indexOf(d.id) !== -1 && isOrphanedRemovable(d));

    const remove = useAsyncAction(async (input: {project: number; ids: number[]; note: string}) => {
        const result = await api.logistics.removeOrphanedInventoryRequests(input);

        if(result.skipped.length > 0) {
            snack.success(`Removed ${result.removed.length} item(s). Skipped ${result.skipped.length} item(s) that are no longer removable.`);
        }

        // un-tick what we just submitted, the removed rows are about to leave the table
        input.ids.forEach(id => setChecked(id, false));

        setOpen(false);
        setNote("");
        refresher();
    }, [refresher, snack, setChecked]);

    if(changeset.proposing) {
        return (
            <Tooltip title="Finish or cancel the change in progress to remove orphaned items">
                <span>
                    <MenuItem disabled>Remove Orphaned</MenuItem>
                </span>
            </Tooltip>
        )
    }

    return <>
        <MenuItem disabled={values.length === 0} onClick={() => {
            props.onOpen();
            setOpen(true);
        }}>Remove Orphaned</MenuItem>

        {open && <Menu open anchorEl={props.anchorEl} onClose={() => setOpen(false)}>
            <Loader {...remove} />
            <div style={{padding: 16, maxWidth: 400}}>
                This will archive {values.length} logistics item(s) left behind by removed openings.
                It is not tracked as a project change and it does not change the customer price.
                <div style={{height: 16}} />
                <TextField onChange={e => setNote(e.target.value)} value={note} fullWidth label="Reason (required)" />
            </div>
            <Grid container spacing={1} justifyContent="flex-end" style={{paddingRight: 10, paddingBottom: 8}}>
                <Grid item>
                    <Button onClick={() => setOpen(false)} size="small">Cancel</Button>
                </Grid>
                <Grid item>
                    <Button onClick={() => remove.callback({
                        project: project,
                        ids: values.map(v => v.id),
                        note: note,
                    })} disabled={!note.trim()} variant="contained" size="small">Remove</Button>
                </Grid>
            </Grid>
        </Menu>}
    </>
}
